import { axiosClient } from '@/features/Client/Client'
import {
  AuthTokens, Identities, ResponseTypes,
  SignInResponses, SignInWithPK, SignType,
  SignUpResponse, SignUpWithPK, VerifyEmailResponses,
  VerifyPublicKey, VerifyPublicKeyMessage, VerifyPublicKeyResponse,
} from '@/features/Auth/auth.types'
import { ApiRoutes } from '@/routes'
import { walletSignMessageAndConvertToBs58 } from '@/shared/helpers/crypto'
import { VersionedTransaction } from '@solana/web3.js'
import bs58 from 'bs58'

export const authService = {
  // verify by tx
  verifyPublicKeyByTx: async ({ dataForSign, signTx, userId, publicKey }: VerifyPublicKey): Promise<VerifyPublicKeyResponse> => {
    const deserializedTx = VersionedTransaction.deserialize(bs58.decode(dataForSign))
    const signedTx = await signTx(deserializedTx)
    const messageToSend = bs58.encode(signedTx.serialize())

    const reqToVerify = await axiosClient.post<VerifyPublicKeyResponse>(ApiRoutes.AUTH_VERIFY_PK, {
      userId,
      publicKey,
      challenge: {
        rawData: dataForSign,
        dataForValidate: messageToSend,
        dataType: SignType.TRANSACTION,
      },
    })

    return reqToVerify.data
  },

  // verify by message
  verifyPublicKeyByMessage: async ({ dataForSign, userId, publicKey, signMessage }: VerifyPublicKeyMessage): Promise<VerifyPublicKeyResponse> => {
    const messageToSend = await walletSignMessageAndConvertToBs58({ data: dataForSign, signMessage })

    const request = await axiosClient.post<VerifyPublicKeyResponse>(ApiRoutes.AUTH_VERIFY_PK, {
      userId,
      publicKey,
      challenge: {
        rawData: dataForSign,
        dataForValidate: messageToSend,
        dataType: SignType.MESSAGE,
      },
    })

    return request.data
  },

  // sign in
  signInWithPublicKey: async ({ publicKey, confirmType = SignType.MESSAGE, signTx, signMessage }: SignInWithPK) => {
    const reqToSignIn = await axiosClient.post<SignInResponses>(ApiRoutes.AUTH_SIGN_IN_PK, {
      publicKey,
      dataTypeForSign: confirmType,
    })

    const { type } = reqToSignIn.data

    if (type === ResponseTypes.SWPO_WITH_SECOND_FACTOR || type === ResponseTypes.SWPO_WITHOUT_SECOND_FACTOR) {
      return reqToSignIn.data
    }

    if (type === ResponseTypes.SETUP_TOTP_IMMEDIATELY) {
      return reqToSignIn.data
    }

    if (type === ResponseTypes.WITH_SIGNING) {
      const { challenge, userId } = reqToSignIn.data

      if (challenge.dataType === SignType.MESSAGE) {
        const reqToVerify = await authService.verifyPublicKeyByMessage({
          dataForSign: challenge.rawData,
          signMessage,
          userId,
          publicKey,
        })

        // without 2fa
        if (reqToVerify.type === ResponseTypes.VerifyPublicKeyResponse) {
          return {
            publicKey,
            accessToken: reqToVerify.accessToken,
            refreshToken: reqToVerify.refreshToken,
          }
        }

        // 2fa for embedded browsers
        if (reqToVerify.type === ResponseTypes.VerifyPublicKeyForEmbeddedBrowser) {
          return { requiredSecondFactor: Identities.TOTP, userId, type: ResponseTypes.VerifyPublicKeyForEmbeddedBrowser }
        }

        // with 2fa
        return { requiredSecondFactor: reqToVerify.requiredSecondFactor, userId }
      }

      const reqToVerify = await authService.verifyPublicKeyByTx({
        dataForSign: challenge.rawData,
        userId,
        publicKey,
        signTx,
      })

      if (reqToVerify.type === ResponseTypes.VerifyPublicKeyResponse) {
        return {
          publicKey,
          accessToken: reqToVerify.accessToken,
          refreshToken: reqToVerify.refreshToken,
        }
      }

      return { requiredSecondFactor: reqToVerify.requiredSecondFactor, userId }
    }
  },

  // sign up
  signUpWithPublicKey: async ({ publicKey, signTx, signMessage, confirmType = SignType.MESSAGE }: SignUpWithPK) => {
    const reqToSignUp = await axiosClient.post<SignUpResponse>(ApiRoutes.AUTH_SIGN_UP_PK, {
      publicKey,
      dataTypeForSign: confirmType,
    })

    const { userId, challenge } = reqToSignUp.data

    if (confirmType === SignType.MESSAGE) {
      const res = await authService.verifyPublicKeyByMessage({
        dataForSign: challenge.rawData,
        userId,
        publicKey,
        signMessage,
      })

      if ('accessToken' in res) {
        return {
          publicKey,
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
        }
      }
    }

    const res = await authService.verifyPublicKeyByTx({
      dataForSign: challenge.rawData,
      userId,
      publicKey,
      signTx,
    })

    if ('accessToken' in res) {
      return {
        publicKey,
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
      }
    }
  },

  // sign out
  signOut: async (refreshToken: string) => {
    await axiosClient.post(ApiRoutes.AUTH_LOGOUT, {
      refreshToken,
    })
  },

  // refresh token
  refreshToken: async (refToken: string) => {
    const req = await axiosClient.post<AuthTokens>(ApiRoutes.AUTH_REFRESH_TOKEN, {
      refreshToken: refToken,
    })

    const { accessToken, refreshToken } = req.data

    return {
      accessToken,
      refreshToken,
    }
  },

  // email verification
  pubEmailVerificaion: async (userId: string) => {
    const req = await axiosClient.post<{maskedEmail: string}>(ApiRoutes.PUB_EMAIL_VERIFY_START, {
      userId,
    })

    return req.data
  },

  pubEmailVerificationFinish: async (userId: string, otp: string) => {
    const req = await axiosClient.post(ApiRoutes.PUB_EMAIL_VERIFY_FINISH, {
      userId,
      otp,
    })

    return req.data
  },

  pubEmailLogin: async (email: string, token?: string | null) => {
    const tokenHeaders = token && {
      'Go-Defi-CF-Token': token,
    }

    const req = await axiosClient.post<{ userId: string }>(ApiRoutes.AUTH_SIGN_IN_EMAIL, {
      email,
    }, {
      headers: tokenHeaders,
    })

    return req.data
  },

  pubEmailLoginFinish: async (userId: string, email: string, otp: string): Promise<VerifyEmailResponses> => {
    const req = await axiosClient.post<VerifyEmailResponses>(ApiRoutes.AUTH_VERIFY_EMAIL, {
      userId,
      email,
      otp,
    })

    return req.data
  },
}
